import React, { useCallback, useMemo, useState } from "react";
import { FieldArray, Form } from "formik";
import { Popconfirm, Row, Tooltip } from "antd";
import styled from "styled-components";
import * as R from "ramda";
import { useSelector } from "react-redux";

import Button from "../../../../../components/Button";
import ConfirmButton from "../../../../../components/ConfirmButton/ConfirmButton";
import * as gtag from "../../../../../lib/gtag";
import { FETCH_TESTS } from "../../../../../store/actions/catalogs";

import EvaluationModal from "./EvaluationModal";
import DocumentRequirementModal from "./DocumentRequirementModal";
import QuestionModal from "./QuestionModal";
import ScreeningTable from "./ScreeningTable";
import {
  disableInvalidQuestions,
  hasInvalidScreeningQuestions,
  hasInvalidScreeningConfig,
  questionHasError
} from "./ScreeningTable/constants";

const AddQuestionBtn = styled(Button)`
  margin: 25px 0 0;
  width: 220px;
`;

const ActionsRow = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
`;

const chatbotQuestionIsDisabled = R.allPass([
  R.propEq("configurable", true),
  R.propEq("type", "chatbot"),
  R.propEq("enabled", false),
  question => R.not(question.isEmpty && question.isEmpty(question)),
  R.compose(
    R.not,
    questionHasError
  )
]);

function Table({
  addQuestionsEnabled = true,
  arrayHelpers,
  handleSubmit,
  isDisabled,
  loading,
  newOpportunity,
  uploadOpportunityDocument,
  setFieldValue,
  isScreeningDisabled,
  setDisabled,
  t,
  values,
  initialValues,
  resetForm,
  isEditingScreening,
  ...props
}) {
  const opportunity = useSelector(({ opportunity }) => opportunity.selected);
  const tests = useSelector(state => FETCH_TESTS.getResponse(state) || []);
  const [questionModal, setQuestionModal] = useState({
    isVisible: false,
    questionIndex: undefined,
    kind: "question"
  });

  const updateIndex = useCallback(
    (index, value) => arrayHelpers.replace(index, value),
    [arrayHelpers]
  );

  const updateOption = useCallback(
    (option, value) =>
      updateIndex(
        R.findIndex(R.propEq("type", option.type), values.questions),
        { ...option, ...value }
      ),
    [updateIndex, values.questions]
  );

  const updateModalOption = useCallback(
    question => updateIndex(questionModal.questionIndex, question),
    [questionModal.questionIndex, updateIndex]
  );

  const addOption = useCallback(option => arrayHelpers.push(option), [
    arrayHelpers
  ]);

  const openDocumentRequirementModal = useCallback(() => {
    const existingDocumentRequirementIndex = R.findIndex(
      R.propEq("type", "document_upload"),
      values.questions
    );

    setQuestionModal({
      isVisible: true,
      kind: "document_upload",
      questionIndex:
        existingDocumentRequirementIndex >= 0
          ? existingDocumentRequirementIndex
          : undefined
    });
  }, [values.questions]);

  const removeOption = useCallback(
    option => arrayHelpers.remove(option.index),
    [arrayHelpers]
  );

  const handleSubmitDisableErrors = useCallback(() => {
    const updatedValues = R.map(disableInvalidQuestions, values.questions);
    setFieldValue("questions", updatedValues);
    handleSubmit();
  }, [handleSubmit, setFieldValue, values.questions]);

  const handleSubmitEnableCompletedChatbot = useCallback(() => {
    const updatedValues = R.map(
      R.when(chatbotQuestionIsDisabled, R.assoc("enabled", true)),
      values.questions
    );
    setFieldValue("questions", updatedValues);
    handleSubmit();
  }, [handleSubmit, setFieldValue, values.questions]);

  const invalidEnabledScreeningQuestions = useMemo(
    () => R.any(hasInvalidScreeningQuestions)(values.questions),
    [values.questions]
  );

  const invalidScreeningQuestions = useMemo(
    () => R.any(hasInvalidScreeningConfig)(values.questions),
    [values.questions]
  );

  const validScreeningQuestionIsDisabled = useMemo(
    () => R.any(chatbotQuestionIsDisabled)(values.questions),
    [values.questions]
  );

  const isEqual = useMemo(
    () =>
      R.equals(
        {
          ...initialValues,
          questions: initialValues.questions.map(q =>
            R.pipe(
              R.dissoc("error"),
              q.type === "video" || q.type === "multiple" || q.type === "number"
                ? R.dissoc("enabled")
                : R.identity,
              q.type === "video" || q.type === "multiple" || q.type === "number"
                ? R.dissoc("id")
                : R.identity
            )(q)
          )
        },
        {
          ...values,
          questions: values.questions.map(q =>
            R.pipe(
              R.dissoc("key"),
              R.dissoc("index"),
              R.dissoc("interaction"),
              R.dissoc("error"),
              q.type === "video" || q.type === "multiple" || q.type === "number"
                ? R.dissoc("enabled")
                : R.identity,
              q.type === "video" || q.type === "multiple" || q.type === "number"
                ? R.dissoc("id")
                : R.identity
            )(q)
          )
        }
      ),
    [initialValues, values]
  );

  const modalQuestion =
    typeof questionModal.questionIndex === "number"
      ? values.questions[questionModal.questionIndex]
      : undefined;

  const modalKind =
    questionModal.kind ||
    (modalQuestion?.type === "test" ? "evaluation" : "question");

  return (
    <>
      <ScreeningTable
        removeOption={removeOption}
        updateOption={updateOption}
        setQuestionModal={setQuestionModal}
        setFieldValue={setFieldValue}
        isDisabled={isDisabled}
        t={t}
        values={values}
        isEditingScreening={isEditingScreening}
        {...props}
      />
      {((addQuestionsEnabled && !isScreeningDisabled) || newOpportunity) && (
        <ActionsRow>
          <AddQuestionBtn
            data-test="add-question-btn"
            onClick={() => {
              gtag.event({
                category: "VacancyDetail",
                action: "VacancyDetail.Screening-Add",
                label: opportunity.id
              });
              setQuestionModal({ isVisible: true, kind: "question" });
            }}
            className="add-question-btn"
          >
            {t("opportunity:screening.questionnaires")}
          </AddQuestionBtn>
          <AddQuestionBtn
            data-test="add-evaluation-btn"
            onClick={() => {
              gtag.event({
                category: "VacancyDetail",
                action: "VacancyDetail.Screening-AddEvaluation",
                label: opportunity.id
              });
              setQuestionModal({ isVisible: true, kind: "evaluation" });
            }}
            className="add-evaluation-btn"
          >
            {t("opportunity:screening.evaluations")}
          </AddQuestionBtn>
          <AddQuestionBtn
            data-test="add-document-requirement-btn"
            onClick={() => {
              gtag.event({
                category: "VacancyDetail",
                action: "VacancyDetail.Screening-AddDocumentRequirement",
                label: opportunity.id
              });
              openDocumentRequirementModal();
            }}
            className="add-document-requirement-btn"
          >
            Documentación
          </AddQuestionBtn>
        </ActionsRow>
      )}

      {!isDisabled &&
        ((!isScreeningDisabled && !newOpportunity) || newOpportunity) && (
          <Row
            type="flex"
            justify={newOpportunity ? "space-between" : "end"}
            style={{ marginTop: "24px" }}
          >
            {newOpportunity && (
              <Button
                size="lg"
                onClick={() => {
                  if (newOpportunity) setFieldValue("advance", false);
                  handleSubmit();
                }}
                disabled={loading}
                type="quaternary"
              >
                {t("global:back")}
              </Button>
            )}
            {!newOpportunity && (
              <Popconfirm
                disabled={isEqual}
                title={t("global:areYouSureScreening")}
                onConfirm={() => {
                  resetForm();
                  setDisabled(true);
                }}
                okText={t("global:yes")}
                cancelText={t("global:no")}
              >
                <Button
                  size="lg"
                  style={{ marginRight: 10 }}
                  type="quaternary"
                  onClick={() => isEqual && setDisabled(true)}
                >
                  {t("global:closeEditingMode")}
                </Button>
              </Popconfirm>
            )}
            <Tooltip
              title={
                invalidScreeningQuestions &&
                t("opportunity:screening.invalidScreeningConfig")
              }
            >
              <span>
                <ConfirmButton
                  popProps={{
                    onConfirm: () => {
                      if (newOpportunity) setFieldValue("advance", true);
                      if (invalidEnabledScreeningQuestions)
                        handleSubmitDisableErrors();
                      else if (validScreeningQuestionIsDisabled)
                        handleSubmitEnableCompletedChatbot();
                    },
                    okButtonProps: { "data-test": "continue-save" },
                    onCancel: () => {
                      if (
                        validScreeningQuestionIsDisabled &&
                        !invalidEnabledScreeningQuestions
                      ) {
                        if (newOpportunity) setFieldValue("advance", true);
                        handleSubmit();
                      }
                    },
                    disabled: R.compose(
                      R.not,
                      R.any(Boolean)
                    )([
                      invalidEnabledScreeningQuestions,
                      validScreeningQuestionIsDisabled
                    ]),
                    title: invalidEnabledScreeningQuestions
                      ? t("opportunity:screening.invalidConfigWarning")
                      : validScreeningQuestionIsDisabled
                      ? t("opportunity:screening.validChatbotIsDisabled")
                      : "",
                    okText: invalidEnabledScreeningQuestions
                      ? t("global:continue")
                      : validScreeningQuestionIsDisabled
                      ? t("global:enable")
                      : "",
                    cancelText: invalidEnabledScreeningQuestions
                      ? t("global:return")
                      : validScreeningQuestionIsDisabled
                      ? t("opportunity:screening.continueWithDisabledChatbot")
                      : ""
                  }}
                  buttonProps={{
                    "data-test": newOpportunity
                      ? "next-step-button"
                      : "save-screening-questions-button",
                    type: "primary",
                    size: "lg",
                    loading,
                    disabled:
                      invalidScreeningQuestions || (isEqual && !newOpportunity),
                    onClick: () => {
                      if (newOpportunity) setFieldValue("advance", true);
                      handleSubmit();
                    },
                    htmlType: "submit"
                  }}
                >
                  {newOpportunity ? t("global:next") : t("global:save")}
                </ConfirmButton>
              </span>
            </Tooltip>
          </Row>
        )}
      {questionModal.isVisible &&
        (modalKind === "evaluation" ? (
          <EvaluationModal
            question={modalQuestion}
            t={t}
            tests={tests}
            onCancel={() =>
              setQuestionModal({
                isVisible: false,
                kind: "question"
              })
            }
            saveQuestion={
              typeof questionModal.questionIndex === "number"
                ? updateModalOption
                : addOption
            }
          />
        ) : modalKind === "document_upload" ||
          (modalQuestion && modalQuestion.type === "document_upload") ? (
          <DocumentRequirementModal
            question={modalQuestion}
            idOpportunity={opportunity.id}
            uploadTemplate={uploadOpportunityDocument}
            onCancel={() =>
              setQuestionModal({
                isVisible: false,
                kind: "question"
              })
            }
            saveQuestion={
              typeof questionModal.questionIndex === "number"
                ? updateModalOption
                : addOption
            }
          />
        ) : (
          <QuestionModal
            question={modalQuestion}
            t={t}
            onCancel={() =>
              setQuestionModal({
                isVisible: false,
                kind: "question"
              })
            }
            saveQuestion={
              typeof questionModal.questionIndex === "number"
                ? updateModalOption
                : addOption
            }
          />
        ))}
    </>
  );
}

function FormWrapper(props) {
  return (
    <Form data-test="questions-form">
      <FieldArray
        name="questions"
        render={arrayHelpers => (
          <Table arrayHelpers={arrayHelpers} {...props} />
        )}
      />
    </Form>
  );
}

export default FormWrapper;
